import React from "react";
import PublicNavbar from "../PublicNavbar";
import LegalFooter from "./LegalFooter";

const PrivacyPolicy = () => {
  return (
    <div className="min-h-screen bg-[#070B14] text-white">
      <PublicNavbar />
      
      <div className="pt-32 pb-20 px-6">
        <div className="max-w-4xl mx-auto">
          <h1 className="text-4xl md:text-5xl font-black tracking-tight mb-4">Privacy Policy</h1>
          <p className="text-gray-400 mb-8">Last Updated: February 17, 2026</p>
          
          <div className="prose prose-invert prose-lg max-w-none space-y-8">
            
            <section>
              <h2 className="text-2xl font-bold text-white mb-4">Introduction</h2>
              <p className="text-gray-300 leading-relaxed">
                Paladin Health & Human Performance ("Paladin," "we," "us," or "our") respects your privacy and is committed to protecting your personal information. This Privacy Policy describes how we collect, use, disclose, and safeguard your information when you use our digital training platform and related services (the "Service"). Please read this Privacy Policy carefully. By using the Service, you consent to the practices described in this policy.
              </p>
            </section>

            <section>
              <h2 className="text-2xl font-bold text-white mb-4">1. Information We Collect</h2>
              
              <h3 className="text-xl font-semibold text-white mt-6 mb-3">Personal Information</h3>
              <p className="text-gray-300 leading-relaxed mb-4">
                We collect personal information that you voluntarily provide when you:
              </p>
              <ul className="list-disc list-inside text-gray-300 space-y-2 mb-4">
                <li>Create an account (name, email address, password)</li>
                <li>Subscribe to our services (billing address, payment information)</li>
                <li>Complete your profile (age, height, weight, fitness goals)</li>
                <li>Contact us for support (any information you provide in communications)</li>
              </ul>

              <h3 className="text-xl font-semibold text-white mt-6 mb-3">Payment Information</h3>
              <p className="text-gray-300 leading-relaxed">
                Payment information (such as credit card numbers) is processed directly by our third-party payment processor (Stripe). We do not store complete payment card information on our servers. We may receive and store limited payment information such as the last four digits of your card number and billing address for record-keeping and customer service purposes.
              </p>

              <h3 className="text-xl font-semibold text-white mt-6 mb-3">Performance and Training Data</h3>
              <p className="text-gray-300 leading-relaxed">
                When you use the Service, we collect data related to your training activities, including:
              </p>
              <ul className="list-disc list-inside text-gray-300 space-y-2 mb-4">
                <li>Workout logs and exercise history</li>
                <li>Performance metrics (weights, reps, times, distances)</li>
                <li>Personal records and progress data</li>
                <li>Program completion status</li>
                <li>Any notes or comments you add to your workouts</li>
              </ul>

              <h3 className="text-xl font-semibold text-white mt-6 mb-3">Usage and Device Data</h3>
              <p className="text-gray-300 leading-relaxed">
                We automatically collect certain information when you access the Service:
              </p>
              <ul className="list-disc list-inside text-gray-300 space-y-2">
                <li>IP address and general location information</li>
                <li>Device type, operating system, and browser type</li>
                <li>Pages visited and features used</li>
                <li>Time and date of access</li>
                <li>Referring website or application</li>
                <li>Click patterns and navigation paths</li>
              </ul>
            </section>

            <section>
              <h2 className="text-2xl font-bold text-white mb-4">2. Cookies and Tracking Technologies</h2>
              <p className="text-gray-300 leading-relaxed mb-4">
                We use cookies and similar tracking technologies to collect and store information about your interactions with the Service. These technologies help us:
              </p>
              <ul className="list-disc list-inside text-gray-300 space-y-2 mb-4">
                <li>Keep you logged in to your account</li>
                <li>Remember your preferences and settings</li>
                <li>Understand how you use the Service</li>
                <li>Improve and optimize the Service</li>
                <li>Deliver relevant content and features</li>
              </ul>
              <p className="text-gray-300 leading-relaxed">
                You can control cookies through your browser settings. However, disabling cookies may limit your ability to use certain features of the Service.
              </p>
            </section>

            <section>
              <h2 className="text-2xl font-bold text-white mb-4">3. Analytics</h2>
              <p className="text-gray-300 leading-relaxed">
                We use analytics tools to help us understand how users interact with the Service. These tools may collect information such as how often you visit, which features you use, and what other sites you visited before coming to our Service. We use this information to improve the Service and user experience.
              </p>
            </section>

            <section>
              <h2 className="text-2xl font-bold text-white mb-4">4. How We Use Your Information</h2>
              <p className="text-gray-300 leading-relaxed mb-4">
                We use the information we collect to:
              </p>
              <ul className="list-disc list-inside text-gray-300 space-y-2">
                <li>Provide, maintain, and improve the Service</li>
                <li>Process transactions and manage your subscription</li>
                <li>Personalize your experience and deliver tailored content</li>
                <li>Track your progress and provide performance analytics</li>
                <li>Communicate with you about your account, updates, and promotional offers</li>
                <li>Respond to your inquiries and provide customer support</li>
                <li>Monitor and analyze usage patterns and trends</li>
                <li>Detect, prevent, and address technical issues and security threats</li>
                <li>Comply with legal obligations</li>
                <li>Enforce our Terms of Service</li>
              </ul>
            </section>

            <section>
              <h2 className="text-2xl font-bold text-white mb-4">5. Information Sharing</h2>
              <p className="text-gray-300 leading-relaxed mb-4">
                We may share your information in the following circumstances:
              </p>
              
              <h3 className="text-xl font-semibold text-white mt-6 mb-3">Service Providers</h3>
              <p className="text-gray-300 leading-relaxed mb-4">
                We share information with third-party service providers who perform services on our behalf, such as payment processing, email delivery, hosting, analytics, and customer support. These providers are contractually obligated to protect your information and use it only for the purposes we specify.
              </p>

              <h3 className="text-xl font-semibold text-white mt-6 mb-3">Legal Requirements</h3>
              <p className="text-gray-300 leading-relaxed mb-4">
                We may disclose your information if required to do so by law or in response to valid legal requests, such as subpoenas, court orders, or government regulations.
              </p>

              <h3 className="text-xl font-semibold text-white mt-6 mb-3">Business Transfers</h3>
              <p className="text-gray-300 leading-relaxed mb-4">
                If we are involved in a merger, acquisition, or sale of assets, your information may be transferred as part of that transaction. We will notify you of any such change and any choices you may have regarding your information.
              </p>

              <h3 className="text-xl font-semibold text-white mt-6 mb-3">No Sale of Personal Data</h3>
              <p className="text-gray-300 leading-relaxed">
                <strong className="text-white">We do not sell your personal information to third parties.</strong> We may share aggregated, anonymized data that does not identify you personally for research, marketing, or other purposes.
              </p>
            </section>

            <section>
              <h2 className="text-2xl font-bold text-white mb-4">6. Data Retention</h2>
              <p className="text-gray-300 leading-relaxed">
                We retain your personal information for as long as your account is active or as needed to provide you with the Service. We may also retain and use your information as necessary to comply with legal obligations, resolve disputes, and enforce our agreements. If you delete your account, we will delete or anonymize your personal information within a reasonable timeframe, except as required by law.
              </p>
            </section>

            <section>
              <h2 className="text-2xl font-bold text-white mb-4">7. Data Security</h2>
              <p className="text-gray-300 leading-relaxed">
                We implement appropriate technical and organizational security measures designed to protect your personal information against unauthorized access, alteration, disclosure, or destruction. These measures include encryption, secure servers, access controls, and regular security assessments. However, no method of transmission over the Internet or electronic storage is 100% secure, and we cannot guarantee absolute security.
              </p>
            </section>

            <section>
              <h2 className="text-2xl font-bold text-white mb-4">8. Your Rights and Choices</h2>
              <p className="text-gray-300 leading-relaxed mb-4">
                Depending on your location, you may have certain rights regarding your personal information:
              </p>
              <ul className="list-disc list-inside text-gray-300 space-y-2 mb-4">
                <li><strong className="text-white">Access:</strong> Request a copy of the personal information we hold about you</li>
                <li><strong className="text-white">Correction:</strong> Request correction of inaccurate or incomplete information</li>
                <li><strong className="text-white">Deletion:</strong> Request deletion of your personal information</li>
                <li><strong className="text-white">Opt-out:</strong> Opt out of marketing communications at any time</li>
                <li><strong className="text-white">Data Portability:</strong> Request a copy of your data in a portable format</li>
              </ul>
              <p className="text-gray-300 leading-relaxed">
                To exercise these rights, please contact us at support@paladinhhp.com. We will respond to your request within a reasonable timeframe and in accordance with applicable law.
              </p>
            </section>

            <section>
              <h2 className="text-2xl font-bold text-white mb-4">9. Children's Privacy</h2>
              <p className="text-gray-300 leading-relaxed">
                The Service is not intended for children under the age of 13. We do not knowingly collect personal information from children under 13. If you are a parent or guardian and believe your child has provided us with personal information without your consent, please contact us immediately. If we become aware that we have collected personal information from a child under 13 without verification of parental consent, we will take steps to delete that information. For users between 13 and 18, parental or guardian consent is required as outlined in our Terms of Service.
              </p>
            </section>

            <section>
              <h2 className="text-2xl font-bold text-white mb-4">10. International Users</h2>
              <p className="text-gray-300 leading-relaxed">
                The Service is operated from the United States. If you are accessing the Service from outside the United States, please be aware that your information may be transferred to, stored, and processed in the United States, where our servers are located and our central database is operated. By using the Service, you consent to the transfer of your information to the United States, which may have different data protection laws than your country of residence.
              </p>
            </section>

            <section>
              <h2 className="text-2xl font-bold text-white mb-4">11. Changes to This Privacy Policy</h2>
              <p className="text-gray-300 leading-relaxed">
                We may update this Privacy Policy from time to time. If we make material changes, we will notify you by email or by posting a prominent notice on the Service prior to the changes becoming effective. We encourage you to review this Privacy Policy periodically to stay informed about how we are protecting your information.
              </p>
            </section>

            <section>
              <h2 className="text-2xl font-bold text-white mb-4">12. Contact Us</h2>
              <p className="text-gray-300 leading-relaxed">
                If you have any questions or concerns about this Privacy Policy or our data practices, please contact us at:
              </p>
              <div className="mt-4 p-4 bg-zinc-900/50 rounded-lg border border-white/10">
                <p className="text-white font-semibold">Paladin Health & Human Performance</p>
                <p className="text-gray-400">Washington, United States</p>
                <p className="text-gray-400">Email: support@paladinhhp.com</p>
              </div>
            </section>

          </div>
        </div>
      </div>
      
      <LegalFooter />
    </div>
  );
};

export default PrivacyPolicy;
